import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { Base } from ".";
import { useNavigate } from "react-router-dom";
import JobType from "../../types/jobType";
import { useDispatch } from "../../store";
import { getJobTypeId, setCurrentViewJobType } from "../../slices/jobType";
import { MoreHoriz } from "@mui/icons-material";
import IconSwitcher, { FilledIconName } from "./IconSwitcher";
import JobStatus from "../../types/jobStatus";

interface Props {
  jobType: JobType;
  statusCounts: { jobStatus: JobStatus; count: number }[];
  closedTotal?: number;
  loading: boolean;
  example?: boolean;
}

const DashboardItem: React.FC<Props> = (props) => {
  const { jobType, statusCounts, closedTotal, loading, example } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const cardTextDisplay = (
    title: string,
    total?: number,
    secondaryTitle?: string,
    center?: boolean,
  ) => (
    <Grid size={{ xs: 4 }} key={title}>
      <Grid
        container
        direction={"column"}
        alignItems={"center"}
        sx={
          center
            ? {
                borderRight: "1px solid grey",
                borderLeft: "1px solid grey",
              }
            : {}
        }
      >
        <Grid
          sx={{
            fontWeight: "bold",
          }}
        >
          <Base.Text text={`${total ?? 0}`} variant="h3" />
        </Grid>
        <Grid>
          <Base.Text text={title} center variant="subtitle1" />
          <Base.Text text={secondaryTitle ?? ""} center variant="subtitle1" />
        </Grid>
      </Grid>
    </Grid>
  );

  const showExtra = (counts: { jobStatus: JobStatus; count: number }[]) => {
    return (
      <Grid size={{ xs: 4 }}>
        <Grid container direction={"column"} alignItems={"center"}>
          <Grid
            sx={{
              fontWeight: "bold",
            }}
          >
            <Tooltip
              title={
                <Box>
                  {counts.slice(2).map((sc, i) => (
                    <Typography
                      key={i}
                    >{`${sc.jobStatus.name}: ${sc.count}`}</Typography>
                  ))}
                  {!!jobType.showRecentlyClosed && (
                    <Typography>{`Recently Closed: ${closedTotal}`}</Typography>
                  )}
                </Box>
              }
            >
              <MoreHoriz />
            </Tooltip>
          </Grid>
          <Grid>
            <Base.Text text={"View More"} center variant="subtitle1" />
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const countDisplay = (counts: { jobStatus: JobStatus; count: number }[]) => {
    if (
      (counts.length <= 2 && !!jobType.showRecentlyClosed) ||
      (counts.length <= 3 && !jobType.showRecentlyClosed)
    ) {
      return (
        <>
          {counts?.map((sc, index) => {
            return cardTextDisplay(
              sc.jobStatus.name ?? "",
              sc.count,
              "",
              index > 0 &&
                (jobType.showRecentlyClosed ||
                  counts.length === 2 ||
                  index < counts.length - 1),
            );
          })}
          {jobType.showRecentlyClosed &&
            counts &&
            counts?.length <= 3 &&
            cardTextDisplay(
              "Recently",
              closedTotal,
              "Closed",
              counts.length === 1,
            )}
        </>
      );
    } else {
      return (
        <>
          {counts.slice(0, 2).map((sc, index) => {
            return cardTextDisplay(
              sc.jobStatus.name ?? "",
              sc.count,
              "",
              index > 0 &&
                (jobType.showRecentlyClosed ||
                  counts.length === 2 ||
                  index < counts.length - 1),
            );
          })}
          {showExtra(counts)}
        </>
      );
    }
  };

  return (
    <Card
      sx={{
        height: 255,
        border: `2px solid ${jobType.color}`,
        borderRadius: 0,
      }}
      elevation={0}
      onClick={() => {
        if (jobType && !example) {
          dispatch(getJobTypeId(jobType.id)).then(({ meta, payload }) => {
            if (meta.requestStatus === "fulfilled") {
              dispatch(setCurrentViewJobType(payload));
              navigate(`/home/${jobType.id}`);
            }
          });
        }
      }}
    >
      <CardActionArea disableRipple sx={{ cursor: "pointer" }}>
        <Box
          sx={{
            height: "155.5px",
            backgroundColor: jobType?.color,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "40%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: theme.palette.primary.contrastText,
          }}
        >
          <IconSwitcher
            name={jobType.icon as FilledIconName}
            style={{ fontSize: 125 }}
          />
        </Box>
        <Box
          sx={{
            width: "100%",
            position: "absolute",
            bottom: "0%",
            left: "50%",
            transform: "translate(-50%, -10%)",
            color: theme.palette.primary.contrastText,
          }}
        >
          <Base.Text text={jobType?.name!} center variant="h3" />
        </Box>
      </CardActionArea>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
          cursor: "pointer",
          backgroundColor: theme.palette.background.paper,
          pt: 2,
          px: 0,
        }}
      >
        {loading ? (
          <CircularProgress size={50} />
        ) : (
          <Grid container direction={"row"} flex={1}>
            {countDisplay(statusCounts)}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardItem;
